import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useStringsContext } from '../../../../../contexts/strings';
import { QuizPoll, QuizPollKeys, QuizRewardComparison, QuizRewardType } from '../../types';
import PollCard from '../poll-card';
import { InputWithValidateProps } from '../../../../common/input/text-input-with-validate';
import {
  getQuizRewardErrors,
  QuizRewardForm,
  toQuizReward,
  toQuizRewardForm,
} from '../../quiz-reward';
import { SxStyle } from '../../../../../types';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormHelperText from '@mui/material/FormHelperText';
import FormLabel from '@mui/material/FormLabel';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

const styles: SxStyle = {
  comparison: {
    width: '150px',
  },
  threshold: {
    width: '120px',
  },
};

const quizRewardKey: QuizPollKeys = 'quizReward';

interface Props extends InputWithValidateProps {
  selectedPoll: QuizPoll | null;
  /** An error that can only be found at save time, e.g. a threshold above the question count */
  thresholdError?: string | null;
}

/**
 * Lets the admin give users who get enough answers right a reward: a message, optionally with
 * a voucher code. The whole reward is one form value, `quizReward`, which is `null` when the
 * switch is off.
 */
const PollQuizRewardCard = ({
  selectedPoll,
  thresholdError,
  onValue,
  onValidation,
  hideValidationError,
}: Props) => {
  const { strings } = useStringsContext();
  const [form, setForm] = useState<QuizRewardForm>(() =>
    toQuizRewardForm(selectedPoll?.quizReward)
  );

  const errors = useMemo(() => getQuizRewardErrors(form), [form]);
  const showErrors = !hideValidationError;
  const thresholdErrorText = (showErrors && errors.threshold) || thresholdError;

  useEffect(() => {
    onValidation?.(quizRewardKey, Object.keys(errors).length === 0);
  }, [errors, onValidation]);

  const updateForm = useCallback(
    (changes: Partial<QuizRewardForm>) => {
      const updatedForm = { ...form, ...changes };
      setForm(updatedForm);
      onValue?.(quizRewardKey, toQuizReward(updatedForm));
    },
    [form, onValue]
  );

  return (
    <PollCard title={strings.polls.quizRewardTitle} label={strings.polls.quizRewardInfo}>
      <Stack spacing={3}>
        <FormControlLabel
          control={
            <Switch
              checked={form.enabled}
              onChange={(event, checked) => updateForm({ enabled: checked })}
            />
          }
          label={strings.polls.quizRewardSwitchLabel}
        />
        {form.enabled && (
          <>
            <Box>
              <Stack direction="row" spacing={2} alignItems="center">
                <FormControl sx={styles.comparison}>
                  <InputLabel id="quiz-reward-comparison-label">
                    {strings.polls.quizRewardComparisonLabel}
                  </InputLabel>
                  <Select
                    labelId="quiz-reward-comparison-label"
                    label={strings.polls.quizRewardComparisonLabel}
                    value={form.comparison}
                    onChange={(event) =>
                      updateForm({ comparison: event.target.value as QuizRewardComparison })
                    }
                  >
                    <MenuItem value="atLeast">{strings.polls.quizRewardAtLeast}</MenuItem>
                    <MenuItem value="moreThan">{strings.polls.quizRewardMoreThan}</MenuItem>
                  </Select>
                </FormControl>
                <TextField
                  sx={styles.threshold}
                  type="number"
                  label={strings.polls.quizRewardThresholdLabel}
                  value={form.threshold}
                  onChange={(event) => updateForm({ threshold: event.target.value })}
                  inputProps={{ min: form.comparison === 'moreThan' ? 0 : 1, step: 1 }}
                  error={!!thresholdErrorText}
                />
                <Typography variant="body1">{strings.polls.quizRewardThresholdSuffix}</Typography>
              </Stack>
              {thresholdErrorText && <FormHelperText error>{thresholdErrorText}</FormHelperText>}
            </Box>
            <FormControl>
              <FormLabel>{strings.polls.quizRewardTypeLabel}</FormLabel>
              <RadioGroup
                row
                value={form.type}
                onChange={(event) => updateForm({ type: event.target.value as QuizRewardType })}
              >
                <FormControlLabel
                  value="text"
                  control={<Radio />}
                  label={strings.polls.quizRewardTypeText}
                />
                <FormControlLabel
                  value="voucher"
                  control={<Radio />}
                  label={strings.polls.quizRewardTypeVoucher}
                />
              </RadioGroup>
            </FormControl>
            <TextField
              label={strings.polls.quizRewardMessageLabel}
              value={form.message}
              onChange={(event) => updateForm({ message: event.target.value })}
              error={showErrors && !!errors.message}
              helperText={
                (showErrors && errors.message) || strings.polls.quizRewardMessageHelperText
              }
              multiline
              minRows={3}
              maxRows={6}
              fullWidth
            />
            {form.type === 'voucher' && (
              <TextField
                label={strings.polls.quizRewardVoucherCodeLabel}
                value={form.voucherCode}
                onChange={(event) => updateForm({ voucherCode: event.target.value })}
                error={showErrors && !!errors.voucherCode}
                helperText={
                  (showErrors && errors.voucherCode) ||
                  strings.polls.quizRewardVoucherCodeHelperText
                }
                fullWidth
              />
            )}
          </>
        )}
      </Stack>
    </PollCard>
  );
};

export default PollQuizRewardCard;
